import { useEffect } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';

import { getAnonymousId, trackDemoEvent } from './demoTracking';

interface Props {
    variant: 'missing' | 'expired';
}

const COPY = {
    missing: {
        title: 'This demo link is invalid',
        body: 'You need a personalized link from Collective to view the demo. Start with our signup flow and we’ll send you one.',
    },
    expired: {
        title: 'This demo link has expired',
        body: 'Demo links are valid for a short window. Reach out to us and we’ll send a fresh one.',
    },
} as const;

const START_URL = 'https://www.collective.com/start';

/**
 * `cltvaid` is how www's tracking.js accepts a visitor id from another subdomain: it is the first
 * source it checks, ahead of the cookie. The shared `.collective.com` cookie usually carries the
 * id across on its own, so this is the belt to that braces — it covers the visitor whose browser
 * dropped the cookie, and it wins over a stale one.
 */
function buildStartUrl(anonymousId: string): string {
    return `${START_URL}?cltvaid=${encodeURIComponent(anonymousId)}`;
}

export default function ExpiredPage({ variant }: Props) {
    const copy = COPY[variant];
    // Renders outside DemoApp, so DemoAnalytics is not mounted here — this page has to emit its
    // own events. Kept off `Demo Page Viewed` so the demo funnel's first step stays demo visits.
    useEffect(() => {
        trackDemoEvent('Demo Expired Page Viewed', { variant });
    }, [variant]);

    return (
        <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center" px={3}>
            <Stack spacing={3} alignItems="center" maxWidth={480} textAlign="center">
                <Typography variant="h4">{copy.title}</Typography>
                <Typography color="text.secondary">{copy.body}</Typography>
                <Button
                    variant="contained"
                    href={buildStartUrl(getAnonymousId())}
                    // Beacon + no preventDefault: the click navigates away immediately, which
                    // would cancel a normal fetch.
                    onClick={() =>
                        trackDemoEvent('Demo CTA Clicked', { cta: 'get_started', variant }, { beacon: true })
                    }>
                    Get started
                </Button>
            </Stack>
        </Box>
    );
}
